import { Accessor, createEffect, createResource, onCleanup } from "solid-js";

import type { Channel, Client } from "revolt.js";
import { ConnectionState } from "revolt.js";

import { useClient } from ".";

export interface ChannelVoiceParticipant {
  id: string;
  joined_at: string;
  is_receiving: boolean;
  is_publishing: boolean;
  screensharing: boolean;
  camera: boolean;
}

export interface ChannelVoiceState {
  id: string;
  participants: ChannelVoiceParticipant[];
}

/**
 * Fetch the current call state for a voice-enabled channel.
 */
export async function fetchChannelCallState(
  client: Client,
  channelId: string,
): Promise<ChannelVoiceState | null> {
  const [headerName, headerValue] = client.authenticationHeader;
  const response = await fetch(
    client.api.config.baseURL + `/channels/${channelId}/call_state`,
    {
      headers: {
        [headerName]: headerValue,
      },
    },
  );

  if (response.status === 404) {
    return null;
  }

  if (response.status === 204) {
    return null;
  }

  if (response.status === 400) {
    // Not a voice channel – treat as empty.
    return null;
  }

  if (response.status === 403) {
    // User cannot view this channel.
    return null;
  }

  if (!response.ok) {
    throw new Error(
      `Failed to fetch call state for channel ${channelId}: ${response.status}`,
    );
  }

  const payload = (await response.json()) as ChannelVoiceState | null;
  return payload ?? null;
}

/**
 * Explicitly inform the backend of the caller's publishing state within a call.
 * Helps keep other client voice states in sync when toggling mute locally.
 */
export async function updateChannelPublishingState(
  client: Client,
  channelId: string,
  isPublishing: boolean,
) {
  const [headerName, headerValue] = client.authenticationHeader;
  const response = await fetch(
    client.api.config.baseURL + `/channels/${channelId}/call_state`,
    {
      method: "POST",
      headers: {
        [headerName]: headerValue,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ is_publishing: isPublishing }),
    },
  );

  if (response.status === 403) {
    throw new Error(
      `Not permitted to update publishing state for channel ${channelId}.`,
    );
  }

  if (!response.ok && response.status !== 204) {
    throw new Error(
      `Failed to update call state for channel ${channelId}: ${response.status}`,
    );
  }
}

type UseChannelCallStateOptions = {
  /**
   * Optional fallback poll interval in milliseconds. Set to a positive number
   * if you still want periodic refetches alongside websocket updates.
   */
  pollInterval?: number;
};

/**
 * Track the call state for a channel and poll for updates.
 */
export function useChannelCallState(
  channel: Accessor<Channel | undefined>,
  options: UseChannelCallStateOptions = {},
) {
  const client = useClient();
  const pollInterval = options.pollInterval ?? 0;

  const [callState, controls] = createResource(
    () => {
      const value = channel();
      return value && value.isVoice ? value.id : undefined;
    },
    async (channelId) => {
      return fetchChannelCallState(client(), channelId);
    },
  );

  createEffect(() => {
    const state = callState();
    const currentClient = client();
    if (!state) return;

    for (const participant of state.participants) {
      if (!currentClient.users.has(participant.id)) {
        void currentClient.users.fetch(participant.id).catch(() => undefined);
      }
    }
  });

  createEffect(() => {
    const value = channel();
    if (!value || !value.isVoice) return;

    const clientInstance = client();
    const channelId = value.id;

    const ensureUsers = (participants: ChannelVoiceParticipant[]) => {
      const currentClient = client();
      for (const participant of participants) {
        if (!currentClient.users.has(participant.id)) {
          void currentClient.users.fetch(participant.id).catch(() => undefined);
        }
      }
    };

    const updateParticipants = (
      transform: (
        current: ChannelVoiceState,
      ) => ChannelVoiceState | null | undefined,
    ) => {
      controls.mutate((previous) => {
        const base: ChannelVoiceState = previous?.id === channelId
          ? {
              id: channelId,
              participants: [...(previous.participants ?? [])],
            }
          : { id: channelId, participants: [] };

        const result = transform(base);
        if (!result || !result.participants.length) {
          ensureUsers([]);
          return null;
        }

        ensureUsers(result.participants);

        return {
          id: channelId,
          participants: [...result.participants],
        };
      });
    };

    const handleEvent = (raw: unknown) => {
      const event =
        raw as
        | ({
            type: "VoiceChannelJoin";
            id: string;
            state: ChannelVoiceParticipant;
          })
        | ({
            type: "VoiceChannelLeave";
            id: string;
            user: string;
          })
        | ({
            type: "VoiceChannelMove";
            user: string;
            from?: string | null;
            to?: string | null;
            state?: ChannelVoiceParticipant;
          })
        | ({
            type: "UserVoiceStateUpdate";
            id: string;
            channel_id: string;
            data: {
              id: string;
              is_publishing?: boolean | null;
              camera?: boolean | null;
              screensharing?: boolean | null;
              is_receiving?: boolean | null;
            };
          })
        | { type: "Ready" }
        | { type: string };

      switch (event.type) {
        case "VoiceChannelJoin": {
          if (
            (event as { id: string; state?: ChannelVoiceParticipant }).id !==
              channelId ||
            !(event as { state?: ChannelVoiceParticipant }).state
          )
            return;
          const joinEvent = event as {
            id: string;
            state: ChannelVoiceParticipant;
          };
          updateParticipants((current) => {
            const participants = current.participants.filter(
              (participant) => participant.id !== joinEvent.state.id,
            );
            participants.push(joinEvent.state);
            return { id: channelId, participants };
          });
          break;
        }
        case "VoiceChannelLeave": {
          if ((event as { id: string }).id !== channelId) return;
          const leaveEvent = event as { user: string };
          updateParticipants((current) => {
            const participants = current.participants.filter(
              (participant) => participant.id !== leaveEvent.user,
            );
            return { id: channelId, participants };
          });
          break;
        }
        case "VoiceChannelMove": {
          const moveEvent = event as {
            from?: string | null;
            to?: string | null;
            user: string;
            state?: ChannelVoiceParticipant;
          };
          if (moveEvent.from === channelId) {
            updateParticipants((current) => {
              const participants = current.participants.filter(
                (participant) => participant.id !== moveEvent.user,
              );
              return { id: channelId, participants };
            });
          }

          if (moveEvent.to === channelId && moveEvent.state) {
            updateParticipants((current) => {
              const participants = current.participants.filter(
                (participant) =>
                  participant.id !== (moveEvent.state as ChannelVoiceParticipant).id,
              );
              participants.push(moveEvent.state!);
              return { id: channelId, participants };
            });
          }
          break;
        }
        case "UserVoiceStateUpdate": {
          const stateEvent = event as {
            channel_id: string;
            id: string;
            data: {
              id: string;
              is_publishing?: boolean | null;
              camera?: boolean | null;
              screensharing?: boolean | null;
              is_receiving?: boolean | null;
            };
          };

          if (stateEvent.channel_id !== channelId) return;
          const update = stateEvent.data;
          let updated = false;

          updateParticipants((current) => {
            const participants = current.participants.map((participant) => {
              if (participant.id !== stateEvent.id) return participant;

              updated = true;
              return {
                ...participant,
                is_publishing:
                  update.is_publishing ?? participant.is_publishing,
                camera: update.camera ?? participant.camera,
                screensharing:
                  update.screensharing ?? participant.screensharing,
                is_receiving:
                  update.is_receiving ?? participant.is_receiving,
              };
            });

            if (!updated) {
              return current;
            }

            return { id: channelId, participants };
          });

          if (!updated) {
            void controls.refetch();
          }

          break;
        }
        case "Ready": {
          void controls.refetch();
          break;
        }
      }
    };

    const handleState = (state: ConnectionState) => {
      if (state === ConnectionState.Connected && pollInterval <= 0) {
        void controls.refetch();
      }
    };

    clientInstance.events.on("event", handleEvent);
    clientInstance.events.on("state", handleState);

    let timer: number | undefined;
    if (pollInterval > 0) {
      timer = window.setInterval(() => {
        void controls.refetch();
      }, pollInterval);
    }

    onCleanup(() => {
      clientInstance.events.off("event", handleEvent);
      clientInstance.events.off("state", handleState);
      if (timer) {
        clearInterval(timer);
      }
    });
  });

  return {
    callState,
    refetch: controls.refetch,
    mutate: controls.mutate,
  };
}
